/**
 * Helper functions to determine message sender types
 */

import PropTypes from "prop-types";

// A message arriving over the socket says who sent it in `senderType` and
// carries no `sender` object at all — so `senderType` is read first, the same
// way separateMessages (Utils/utils.js) and normalizeWebSocketMessage
// (Pages/ChatHuman/utils/normalizeMessage.js) read it. `sender.agent` exists
// only after a message has been normalized or fetched from history, so it is
// the fallback, never the test: asking for it first makes every live answer
// look as though it came from nobody.
const AGENT_SENDER_TYPES = ["AGENT", "BOT"];
const CUSTOMER_SENDER_TYPES = ["USER"];

// A conversation's own events — a session notice and the like — arrive with a
// senderType that is neither, and they belong to neither side.
const senderTypeOf = (message) =>
  typeof message?.senderType === "string" ? message.senderType.trim().toUpperCase() : "";

/**
 * PropTypes definition for message objects
 */
export const MessagePropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  content: PropTypes.string,
  sender: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    agent: PropTypes.bool,
    isAgent: PropTypes.bool,
  }),
  // Free text rather than a fixed list: the socket sends values this app does
  // not act on, and a message the app ignores must not raise a warning.
  senderType: PropTypes.string,
  senderName: PropTypes.string,
  senderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
});

/**
 * Determines if a message is from a customer (user)
 * @param {Object} message - The message object
 * @returns {boolean} - True if message is from customer
 */
export const isCustomerMessage = (message) => {
  const senderType = senderTypeOf(message);
  if (senderType) return CUSTOMER_SENDER_TYPES.includes(senderType);

  if (!message?.sender) return false;

  return message.sender.agent !== true && message.sender.isAgent !== true;
};

/**
 * Determines if a message is from an agent
 * @param {Object} message - The message object
 * @returns {boolean} - True if message is from agent
 */
export const isAgentMessage = (message) => {
  const senderType = senderTypeOf(message);
  if (senderType) return AGENT_SENDER_TYPES.includes(senderType);

  return message?.sender?.agent === true || message?.sender?.isAgent === true;
};

/**
 * Determines if a message is from a bot
 * @param {Object} message - The message object
 * @returns {boolean} - True if message is from bot
 */
export const isBotMessage = (message) => {
  return senderTypeOf(message) === "BOT";
};

/**
 * Determines if a message should be displayed on the left side (agent/bot)
 * @param {Object} message - The message object
 * @returns {boolean} - True if message should be on left side
 */
export const isLeftSideMessage = (message) => {
  return isAgentMessage(message) || isBotMessage(message);
};

/**
 * Determines if a message should be displayed on the right side (customer)
 * @param {Object} message - The message object
 * @returns {boolean} - True if message should be on right side
 */
export const isRightSideMessage = (message) => {
  return isCustomerMessage(message);
};

/**
 * Gets the display name for a message sender
 * @param {Object} message - The message object
 * @returns {string} - Display name for the sender
 */
export const getSenderDisplayName = (message) => {
  // The name travels beside the message on a live frame and inside the sender
  // object on a normalized one.
  const name = message?.sender?.name || message?.senderName || null;

  if (isBotMessage(message)) {
    return name || "Bot";
  }

  if (isAgentMessage(message)) {
    return name || "Agent";
  }

  if (isCustomerMessage(message)) {
    return name || "Customer";
  }

  return name || "Unknown";
};

